import { Badge } from "@/components/ui/Badge/Badge";
import { Button } from "@/components/ui/Button/Button";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table";
import { format } from "date-fns";
import {
  ArrowDownAZ,
  ArrowUpAZ,
  ArrowUpDown,
  BookPlus,
  Filter,
} from "lucide-react";
import {
  formatWindow,
  getWindowStatus,
  sortByWindowFrom,
  statusLabel,
  statusVariant,
  windowFilterFn,
} from "@/utils/rotations/window";
import type { WindowStatus } from "@/types/tables";
import { HStack } from "@/components/layout/HStack";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import { useEffect, useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import { RESULTS_PER_PAGE_OPTIONS } from "@/constants/tableOptions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/Pagination/Pagination";
import { buildRotationRows } from "@/utils/rotations/buildRotationRows";
import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { useSearchParams } from "react-router-dom";
import { parseCsv, writeCsv } from "@/utils/csv";
import type { RotationsRow } from "@/types/tables";

const getColumns = (
  onCreate: (rotation: any) => void,
  archived: boolean = false,
): ColumnDef<RotationsRow>[] => [
  {
    accessorKey: "rotationDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Date{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => format(row.original.rotationDate, "MMMM dd, yyyy"),
    sortingFn: "datetime",
  },
  {
    accessorKey: "sessions",
    header: "Sessions",
    cell: ({ row }) => row.original.sessions.join(", "),
  },
  {
    accessorKey: "teachersCanCreate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Teachers Can Create{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => {
      return row.original.teachersCanCreate === true ? (
        <Badge variant="success">Yes</Badge>
      ) : (
        <Badge variant="destructive">No</Badge>
      );
    },
    filterFn: (row, columnId, value: ("yes" | "no")[] | undefined) => {
      if (!value || value.length === 0) return true;
      const v = row.getValue(columnId) as boolean;
      return value.includes(v ? "yes" : "no");
    },
  },
  {
    accessorKey: "studentsCanView",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Students Can View{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => {
      const window = formatWindow(row.original.studentsCanView);
      const status = getWindowStatus(row.original.studentsCanView);

      return (
        <HStack align="center">
          <Badge variant={statusVariant[status]}>{statusLabel[status]}</Badge>
          <span>{window}</span>
        </HStack>
      );
    },
    filterFn: windowFilterFn,
    sortingFn: sortByWindowFrom,
  },
  {
    accessorKey: "studentsCanSignup",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Students Can Signup{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => {
      const window = formatWindow(row.original.studentsCanSignup);
      const status = getWindowStatus(row.original.studentsCanSignup);

      return (
        <HStack align="center">
          <Badge variant={statusVariant[status]}>{statusLabel[status]}</Badge>
          <span>{window}</span>
        </HStack>
      );
    },
    filterFn: windowFilterFn,
    sortingFn: sortByWindowFrom,
  },
  {
    id: "actions",
    header: () => (
      <div style={{ textAlign: "right", fontWeight: "var(--font-medium)" }}>
        Actions
      </div>
    ),
    cell: ({ row }) => {
      const rotation = row.original;

      if (archived) return <HStack justify="flex-end" />;
      return (
        <HStack justify="flex-end">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-small"
                disabled={!rotation.teachersCanCreate}
                onClick={() => onCreate(rotation)}
              >
                <BookPlus />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Create exploration</TooltipContent>
          </Tooltip>
        </HStack>
      );
    },
  },
];

const DataTable = ({
  data,
  columns,
}: {
  data: RotationsRow[];
  columns: ColumnDef<RotationsRow>[];
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Sorting
  const sortKRaw = searchParams.get("sortK");
  const sortVRaw = searchParams.get("sortV");

  const urlSorting = useMemo<SortingState>(() => {
    const validK =
      sortKRaw === "rotationDate" ||
      sortKRaw === "teachersCanCreate" ||
      sortKRaw === "studentsCanView" ||
      sortKRaw === "studentsCanSignup";

    const validV = sortVRaw === "asc" || sortVRaw === "desc";

    if (!validK || !validV) return [];
    return [{ id: sortKRaw, desc: sortVRaw === "desc" }];
  }, [sortKRaw, sortVRaw]);

  const onSortingChange = (
    updater: SortingState | ((old: SortingState) => SortingState),
  ) => {
    const current = urlSorting;
    const next = typeof updater === "function" ? updater(current) : updater;

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);

      const first = next[0];
      if (!first) {
        sp.delete("sortK");
        sp.delete("sortV");
        return sp;
      }

      sp.set("sortK", first.id);
      sp.set("sortV", first.desc ? "desc" : "asc");
      return sp;
    });
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting: urlSorting,
      columnFilters,
      pagination,
    },
  });

  const windowStatusOptions: WindowStatus[] = ["soon", "active", "passed"];

  type TeachersCreateFilter = "yes" | "no";
  const teacherCreateOptions: TeachersCreateFilter[] = ["yes", "no"];

  // Teachers Can Create Filtering
  const urlTeachers = useMemo(
    () => parseCsv(searchParams.get("tcc")) as TeachersCreateFilter[],
    [searchParams],
  );

  const selectedTeachersCanCreate = urlTeachers;

  const availableTeachersCanCreate = useMemo(() => {
    const set = new Set<TeachersCreateFilter>();
    for (const d of data) set.add(d.teachersCanCreate ? "yes" : "no");
    return teacherCreateOptions.filter((v) => set.has(v));
  }, [data]);

  const toggleTeachersCanCreate = (v: TeachersCreateFilter) => {
    const next = selectedTeachersCanCreate.includes(v)
      ? selectedTeachersCanCreate.filter((x) => x !== v)
      : [...selectedTeachersCanCreate, v];

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      writeCsv(sp, "tcc", next);
      return sp;
    });
  };

  const clearTeachersCanCreateFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("tcc");
      return sp;
    });
  };

  // Students Can View Filtering
  const urlView = useMemo(
    () => parseCsv(searchParams.get("view")) as WindowStatus[],
    [searchParams],
  );

  const selectedViewStatuses = urlView;

  const availableViewStatuses = useMemo(() => {
    const set = new Set<WindowStatus>();
    for (const d of data) set.add(getWindowStatus(d.studentsCanView));
    return windowStatusOptions.filter((s) => set.has(s));
  }, [data]);

  const toggleViewStatus = (status: WindowStatus) => {
    const next = selectedViewStatuses.includes(status)
      ? selectedViewStatuses.filter((x) => x !== status)
      : [...selectedViewStatuses, status];

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      writeCsv(sp, "view", next);
      return sp;
    });
  };

  const clearViewFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("view");
      return sp;
    });
  };

  // Students Can Signup Filtering

  const urlSignup = useMemo(
    () => parseCsv(searchParams.get("signup")) as WindowStatus[],
    [searchParams],
  );

  const selectedSignupStatuses = urlSignup;

  const availableSignupStatuses = useMemo(() => {
    const set = new Set<WindowStatus>();
    for (const d of data) set.add(getWindowStatus(d.studentsCanSignup));
    return windowStatusOptions.filter((s) => set.has(s));
  }, [data]);

  const toggleSignupStatus = (status: WindowStatus) => {
    const next = selectedSignupStatuses.includes(status)
      ? selectedSignupStatuses.filter((x) => x !== status)
      : [...selectedSignupStatuses, status];

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      writeCsv(sp, "signup", next);
      return sp;
    });
  };

  const clearSignupFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("signup");
      return sp;
    });
  };

  const totalCount = table.getCoreRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;

  useEffect(() => {
    table.getColumn("teachersCanCreate")?.setFilterValue(urlTeachers);
    table.getColumn("studentsCanView")?.setFilterValue(urlView);
    table.getColumn("studentsCanSignup")?.setFilterValue(urlSignup);
    table.setPageIndex(0);
  }, [table, urlTeachers, urlView, urlSignup]);

  // Pagination

  const showPagination = table.getPageCount() > 1;

  function getPageItems(current: number, total: number) {
    const c = current + 1;
    const t = total;

    if (t <= 7) return Array.from({ length: t }, (_, i) => i + 1);

    const items: (number | "…")[] = [1];

    if (c > 3) items.push("…");

    const start = Math.max(2, c - 1);
    const end = Math.min(t - 1, c + 1);

    for (let p = start; p <= end; p++) items.push(p);

    if (c < t - 2) items.push("…");

    items.push(t);
    return items;
  }

  return (
    <>
      <HStack align="center" justify="space-between" wrap>
        <HStack align="center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                style={{
                  height: "fit-content",
                  padding: "var(--space-xxs) var(--space-xs)",
                }}
              >
                <Filter /> Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  Teachers Can Create
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {availableTeachersCanCreate.map((s) => (
                    <DropdownMenuCheckboxItem
                      key={s}
                      checked={selectedTeachersCanCreate.includes(s)}
                      onCheckedChange={() => toggleTeachersCanCreate(s)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {s === "yes" ? "Yes" : "No"}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      clearTeachersCanCreateFilter();
                    }}
                    disabled={selectedTeachersCanCreate.length === 0}
                  >
                    Clear
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  Students Can View
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {availableViewStatuses.map((s) => (
                    <DropdownMenuCheckboxItem
                      key={s}
                      checked={selectedViewStatuses.includes(s)}
                      onCheckedChange={() => toggleViewStatus(s)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {statusLabel[s]}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      clearViewFilter();
                    }}
                    disabled={selectedViewStatuses.length === 0}
                  >
                    Clear
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  Students Can Signup
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {availableSignupStatuses.map((s) => (
                    <DropdownMenuCheckboxItem
                      key={s}
                      checked={selectedSignupStatuses.includes(s)}
                      onCheckedChange={() => toggleSignupStatus(s)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {statusLabel[s]}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      clearSignupFilter();
                    }}
                    disabled={selectedSignupStatuses.length === 0}
                  >
                    Clear
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
          <p
            style={{
              color: "var(--muted-foreground)",
            }}
          >
            {filteredCount} of {totalCount} rotations
          </p>
        </HStack>
        <Select
          value={String(table.getState().pagination.pageSize)}
          onValueChange={(v) => {
            table.setPageSize(Number(v));
            table.setPageIndex(0);
          }}
        >
          <SelectTrigger
            style={{
              width: "fit-content",
              height: "fit-content",
              padding: "var(--space-xxs) var(--space-xs)",
            }}
          >
            <SelectValue placeholder="Select results per page" />
          </SelectTrigger>
          <SelectContent>
            {RESULTS_PER_PAGE_OPTIONS.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option} results/page
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </HStack>
      <Table borderColor="var(--secondary-border)">
        <TableHeader>
          {table.getHeaderGroups().map((h) => (
            <TableRow key={h.id}>
              {h.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>No records</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {showPagination && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
              />
            </PaginationItem>

            {getPageItems(pagination.pageIndex, table.getPageCount()).map(
              (it, idx) =>
                it === "…" ? (
                  <PaginationItem key={`el-${idx}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={it}>
                    <PaginationButton
                      isActive={it === pagination.pageIndex + 1}
                      onClick={(e) => {
                        e.preventDefault();
                        table.setPageIndex(it - 1);
                      }}
                    >
                      {it}
                    </PaginationButton>
                  </PaginationItem>
                ),
            )}

            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  e.preventDefault();
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
};

const RotationsTable = ({
  data,
  onCreate,
  archived = false,
}: {
  data: { rotations: any[]; sessions: any[] };
  onCreate: (rotation: any) => void;
  archived?: boolean;
}) => {
  const rows = useMemo(() => buildRotationRows(data), [data]);
  const columns = useMemo(
    () => getColumns(onCreate, archived),
    [onCreate, archived],
  );

  return (
    <DashboardCard>
      <DataTable data={rows} columns={columns} />
    </DashboardCard>
  );
};

export { RotationsTable };
